import type { z } from "zod";
import type { EventType } from "./actions.ts";
import type { ConditionType } from "./conditions.ts";
import { StatImpact } from "./entities.ts";

export type StatImpactValue = z.infer<typeof StatImpact>;

export type FieldRequirement = {
	name: string;
	kind: "string" | "number" | "boolean" | "array" | "object" | "enum" | "any";
	required: boolean;
	refEntity?: "items" | "currencies" | "stats" | "recipes" | "dialogues" | "quests";
	minLength?: number;
	min?: number;
	gt?: number;
	values?: readonly string[];
	description?: string;
};

export type DataKind =
	| "number"
	| "string"
	| "color"
	| "boolean"
	| "list"
	| "player"
	| "any";

export const DATA_KINDS = [
	"number",
	"string",
	"color",
	"boolean",
	"list",
	"player",
	"any",
] as const satisfies readonly DataKind[];

export type DataPort = {
	name: string;
	kind: DataKind;
};

export type SelectionKind =
	| "string"
	| "integer"
	| "string[]"
	| "integer[]";

export type SelectionInput = {
	name: string;
	kind: SelectionKind;
	refEntity?: "items" | "currencies" | "stats" | "recipes" | "dialogues" | "quests";
	maxLength?: number;
	maxItems?: number;
	min?: number;
	max?: number;
	allowEmpty?: boolean;
};


export type EventSpec = {
	type: EventType;
	outputHandles: readonly string[];
};

export type ConditionSpec = {
	type: ConditionType;
	fields: FieldRequirement[];
};

export {
	HANDLE_NEXT,
	HANDLE_TRUE,
	HANDLE_FALSE,
	HANDLE_SUCCESS,
	HANDLE_FAILED,
	HANDLE_CANCELLED,
	HANDLE_IN_PROGRESS,
	HANDLE_SUCCEEDED,
	HANDLE_ERROR,
	HANDLE_NOT_STARTED,
	HANDLE_ARRIVED,
	HANDLE_NO_WAY,
	HANDLE_ON_START,
	HANDLE_ON_END,
	HANDLE_ON_EVENT,
	NEXT_ONLY,
} from "./spec/handles.ts";
import { NEXT_ONLY } from "./spec/handles.ts";

export const EVENT_SPECS: EventSpec[] = [
	{ type: "OnSpawn", outputHandles: NEXT_ONLY },
	{ type: "OnTouch", outputHandles: NEXT_ONLY },
	{ type: "OnConsume", outputHandles: NEXT_ONLY },
	{ type: "OnDamage", outputHandles: NEXT_ONLY },
	{ type: "OnDeath", outputHandles: NEXT_ONLY },
	{ type: "OnCreate", outputHandles: NEXT_ONLY },
	{ type: "OnRespawn", outputHandles: NEXT_ONLY },
	{ type: "OnClick", outputHandles: NEXT_ONLY },
	{ type: "OnHover", outputHandles: NEXT_ONLY },
	{ type: "OnUnhover", outputHandles: NEXT_ONLY },
	{ type: "OnMouseDown", outputHandles: NEXT_ONLY },
	{ type: "OnMouseUp", outputHandles: NEXT_ONLY },
	{ type: "OnVisible", outputHandles: NEXT_ONLY },
	{ type: "OnHidden", outputHandles: NEXT_ONLY },
	{ type: "OnEnter", outputHandles: NEXT_ONLY },
	{ type: "OnExit", outputHandles: NEXT_ONLY },
	{ type: "OnZoneEnter", outputHandles: NEXT_ONLY },
	{ type: "OnZoneExit", outputHandles: NEXT_ONLY },
	{ type: "OnMatchReady", outputHandles: NEXT_ONLY },
	{ type: "OnMatchFailed", outputHandles: NEXT_ONLY },
	{ type: "OnTeamWin", outputHandles: NEXT_ONLY },
	{ type: "OnTeamDefeated", outputHandles: NEXT_ONLY },
	{ type: "OnMatchQuit", outputHandles: NEXT_ONLY },
];

const UNIVERSAL_ENTRYPOINTS: readonly EventType[] = ["OnSpawn", "OnTouch"];

const UI_NODE_ENTRYPOINTS = [
	"OnClick",
	"OnHover",
	"OnUnhover",
	"OnMouseDown",
	"OnMouseUp",
	"OnVisible",
	"OnHidden",
] as const satisfies readonly EventType[];
const UI_NODE_ENTRYPOINT_SET: ReadonlySet<EventType> = new Set(UI_NODE_ENTRYPOINTS);

export const CATEGORY_ENTRYPOINTS: Record<string, readonly EventType[]> = {
	Body: [],
	Companion: [],
	Main: [],
	Consumable: ["OnConsume"],
	Collectible: [],
	Player: ["OnCreate"],
	Zone: ["OnEnter", "OnExit"],
	Matchmaker: ["OnMatchReady", "OnMatchFailed", "OnTeamWin", "OnTeamDefeated", "OnMatchQuit"],

	UiNode: UI_NODE_ENTRYPOINTS,
};

export const DEFAULT_ENTRYPOINTS: readonly EventType[] = UNIVERSAL_ENTRYPOINTS;

export const IMPACT_ENTRYPOINTS: Record<StatImpactValue, readonly EventType[]> =
{
	Vitality: ["OnDamage", "OnDeath"],
	Attack: [],
	Defense: [],
	Speed: [],
	Production: [],
	None: [],
};

export const LEGACY_EVENT_TYPES: readonly string[] = [
	"OnInteract",
	"OnDie",
	"OnPlayerJoin",
];

type StatImpactLookup = {
	readonly impact: StatImpactValue | string;
};

const EVENT_ORDER: readonly EventType[] = [
	"OnSpawn",
	"OnTouch",
	"OnConsume",
	"OnDamage",
	"OnDeath",
	"OnCreate",
	"OnRespawn",
	"OnClick",
	"OnMouseDown",
	"OnMouseUp",
	"OnHover",
	"OnUnhover",
	"OnVisible",
	"OnHidden",
	"OnEnter",
	"OnExit",
	"OnZoneEnter",
	"OnZoneExit",
	"OnMatchReady",
	"OnMatchFailed",
	"OnTeamWin",
	"OnTeamDefeated",
	"OnMatchQuit",
];

export const getRequiredEntryPoints = (
	category: string | null | undefined,
	itemStatKeys: readonly string[],
	statDefs: Record<string, StatImpactLookup>,
): readonly EventType[] => {

	if (category === "UiNode") {
		const uiOrder = EVENT_ORDER.filter((ev) =>
			UI_NODE_ENTRYPOINT_SET.has(ev),
		);
		return uiOrder;
	}
	if (category === "Matchmaker") {
		const matchmakerEvents = new Set<EventType>(CATEGORY_ENTRYPOINTS.Matchmaker);
		return EVENT_ORDER.filter((ev) => matchmakerEvents.has(ev));
	}
	const set = new Set<EventType>(UNIVERSAL_ENTRYPOINTS);
	const fromCategory: readonly EventType[] =
		category != null && CATEGORY_ENTRYPOINTS[category] !== undefined
			? CATEGORY_ENTRYPOINTS[category]!
			: DEFAULT_ENTRYPOINTS;
	for (const ev of fromCategory) set.add(ev);
	for (const statId of itemStatKeys) {
		const def = statDefs[statId];
		if (!def) continue;
		const events = IMPACT_ENTRYPOINTS[def.impact as StatImpactValue];
		if (!events) continue;
		for (const ev of events) set.add(ev);
	}
	return EVENT_ORDER.filter((ev) => set.has(ev));
};



export const CONDITION_SPECS: ConditionSpec[] = [
	{
		type: "HasItem",
		fields: [
			{ name: "itemId", kind: "string", required: true, refEntity: "items" },
			{ name: "operator", kind: "string", required: true },
			{ name: "value", kind: "number", required: true },
		],
	},
	{
		type: "HasStat",
		fields: [
			{ name: "statKey", kind: "string", required: true, refEntity: "stats" },
			{ name: "operator", kind: "string", required: true },
			{ name: "value", kind: "number", required: true },
		],
	},
	{
		type: "HasCurrency",
		fields: [
			{ name: "currencyId", kind: "string", required: true, refEntity: "currencies" },
			{ name: "operator", kind: "string", required: true },
			{ name: "value", kind: "number", required: true },
		],
	},
	{
		type: "IsOnZone",
		fields: [
			{ name: "zoneItemId", kind: "string", required: true, refEntity: "items" },
		],
	},
];

export const EVENT_SPEC_BY_TYPE = new Map<EventType, EventSpec>(
	EVENT_SPECS.map((s) => [s.type, s]),
);

export {
	ACTION_SPECS,
	ACTION_SPEC_BY_TYPE,
	type ActionSpec,
	isDataProducer,
	getActionSpec,
	getEventSpec,
	getOutputHandles,
} from "./spec/action.spec.ts";
